import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { HERO, SPECIALTY, CATEGORIES, TESTIMONIALS, PARTNER } from "@/data/content";
import { SERVICE_DETAILS } from "@/data/serviceDetails";
import { SUBURBS } from "@/data/suburbs";

const ContentContext = createContext({ overrides: {}, ready: false, refresh: () => {} });

export const SiteContentProvider = ({ children }) => {
  const [overrides, setOverrides] = useState({});
  const [ready, setReady] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/content");
      setOverrides(data || {});
    } catch {
      setOverrides({});
    } finally {
      setReady(true);
    }
  };

  useEffect(() => { refresh(); }, []);

  return <ContentContext.Provider value={{ overrides, ready, refresh }}>{children}</ContentContext.Provider>;
};

const str = (o, d) => (o !== undefined && o !== null && o !== "" ? o : d);
const arr = (o, d) => (Array.isArray(o) && o.length ? o : d);

export function useSiteContent() {
  const { overrides: o, ready, refresh } = useContext(ContentContext);

  const value = useMemo(() => {
    const oc = o || {};
    const hero = {
      overline: str(oc.hero?.overline, HERO.overline),
      lines: arr(oc.hero?.lines, HERO.lines),
      intro: str(oc.hero?.intro, HERO.intro),
      image: str(oc.hero?.image, HERO.image),
    };
    const specialty = {
      overline: str(oc.specialty?.overline, SPECIALTY.overline),
      title: str(oc.specialty?.title, SPECIALTY.title),
      body: str(oc.specialty?.body, SPECIALTY.body),
      points: arr(oc.specialty?.points, SPECIALTY.points),
    };
    const categories = CATEGORIES.map((c) => {
      const ov = oc.categories?.[c.id] || {};
      return { ...c, title: str(ov.title, c.title), tag: str(ov.tag, c.tag), body: str(ov.body, c.body), image: str(ov.image, c.image) };
    });
    const testimonials = arr(oc.testimonials, TESTIMONIALS);
    const partner = {
      overline: str(oc.partner?.overline, PARTNER.overline),
      name: str(oc.partner?.name, PARTNER.name),
      body: str(oc.partner?.body, PARTNER.body),
      points: arr(oc.partner?.points, PARTNER.points),
    };
    const services = {};
    Object.keys(SERVICE_DETAILS).forEach((slug) => {
      const d = SERVICE_DETAILS[slug];
      const ov = oc.services?.[slug] || {};
      services[slug] = {
        ...d,
        heroKicker: str(ov.heroKicker, d.heroKicker),
        heroTitle: str(ov.heroTitle, d.heroTitle),
        overview: arr(ov.overview, d.overview),
        included: arr(ov.included, d.included),
        faqs: arr(ov.faqs, d.faqs),
        gallery: arr(ov.gallery, d.gallery),
      };
    });
    const suburbs = SUBURBS.map((s) => {
      const ov = oc.suburbs?.[s.slug] || {};
      return { ...s, blurb: str(ov.blurb, s.blurb), meta: str(ov.meta, s.meta) };
    });
    return { hero, specialty, categories, testimonials, partner, services, suburbs };
  }, [o]);

  return { ...value, ready, refresh };
}
